import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Gamepad2, Target, Star, ChevronRight, User, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getPlayerRankings, type RankedPlayer, type RankingType } from '@/services/rankingService';
import { UserAvatar } from '@/components/UserAvatar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SEO } from '@/components/SEO';
import { slugify } from '@/lib/slugify';
import { Button } from '@/components/ui/button';

export default function Ranking() {
    const navigate = useNavigate();
    const [rankingType, setRankingType] = useState<RankingType>('missions');
    const [players, setPlayers] = useState<RankedPlayer[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const limit = 50;

    useEffect(() => {
        const fetchRankings = async () => {
            setLoading(true);
            const { players: data, total: totalCount } = await getPlayerRankings(rankingType, page, limit);
            setPlayers(data);
            setTotal(totalCount);
            setLoading(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        };
        fetchRankings();
    }, [rankingType, page]);

    // Reset page when ranking type changes
    useEffect(() => {
        setPage(1);
    }, [rankingType]);

    const handlePlayerClick = (displayName: string) => {
        navigate(`/p/${slugify(displayName)}`);
    };

    const topThree = page === 1 ? players.slice(0, 3) : [];
    const others = page === 1 ? players.slice(3) : players;
    const totalPages = Math.ceil(total / limit);

    const getIcon = (type: RankingType) => {
        switch (type) {
            case 'missions': return <Star className="w-5 h-5 text-purple-500" />;
            case 'playtime': return <Trophy className="w-5 h-5 text-yellow-500" />;
            case 'tested': return <Gamepad2 className="w-5 h-5 text-green-500" />;
            case 'experience': return <Target className="w-5 h-5 text-blue-500" />;
        }
    };

    const getRankingTitle = (type: RankingType) => {
        switch (type) {
            case 'missions': return 'Missions Done';
            case 'playtime': return 'Total Playtime';
            case 'tested': return 'Games Tested';
            case 'experience': return 'Experience (XP)';
        }
    };

    return (
        <Layout>
            <SEO
                title={`Player Rankings - ${getRankingTitle(rankingType)}`}
                description="Check out the top players on IndieKeys. Rankings by missions completed, playtime, and games tested."
            />

            <div className="container mx-auto px-4 py-8 max-w-5xl">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                            <Trophy className="w-8 h-8 text-primary" />
                            Player Rankings
                        </h1>
                        <p className="text-muted-foreground">
                            Celebrating the most dedicated testers and community members in our community.
                        </p>
                    </div>

                    <Tabs value={rankingType} onValueChange={(v) => setRankingType(v as RankingType)} className="w-full md:w-auto">
                        <TabsList className="flex flex-wrap md:grid md:grid-cols-4 w-full bg-background/50 border border-border/50 !p-1 h-auto gap-1 rounded-custom-paddings">
                            <TabsTrigger
                                value="missions"
                                className="flex-1 md:w-auto text-xs py-2 px-2 md:px-6 rounded-custom-paddings data-[state=active]:bg-card data-[state=active]:text-primary-foreground data-[state=active]:font-semibold transition-all duration-300"
                            >
                                Missions
                            </TabsTrigger>
                            <TabsTrigger
                                value="playtime"
                                className="flex-1 md:w-auto text-xs py-2 px-2 md:px-6 rounded-custom-paddings data-[state=active]:bg-card data-[state=active]:text-primary-foreground data-[state=active]:font-semibold transition-all duration-300"
                            >
                                Playtime
                            </TabsTrigger>
                            <TabsTrigger
                                value="tested"
                                className="flex-1 md:w-full text-xs py-2 px-2 md:px-6 rounded-custom-paddings data-[state=active]:bg-card data-[state=active]:text-primary-foreground transition-all duration-300"
                            >
                                Games Tested
                            </TabsTrigger>
                            <TabsTrigger
                                value="experience"
                                className="flex-1 md:w-full text-xs py-2 px-2 md:px-6 rounded-custom-paddings data-[state=active]:bg-card data-[state=active]:text-primary-foreground transition-all duration-300"
                            >
                                Experience
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 gap-4">
                        {[1, 2, 3, 4, 5].map(i => (
                            <Card key={i} className="h-20 animate-pulse bg-muted/20" />
                        ))}
                    </div>
                ) : players.length === 0 ? (
                    <Card className="p-12 text-center bg-muted/10 border-dashed">
                        <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium">No results found</h3>
                        <p className="text-muted-foreground">The rankings are being recalculated or no players match this criteria yet.</p>
                    </Card>
                ) : (
                    <div className="space-y-12">
                        {/* Podium Section - Desktop Only - Only on Page 1 */}
                        {page === 1 && (
                            <div className="hidden md:grid grid-cols-3 gap-6 items-end pt-12">
                                {/* Second Place */}
                                {topThree[1] && (
                                    <div className="flex flex-col items-center group cursor-pointer" onClick={() => handlePlayerClick(topThree[1].display_name)}>
                                        <div className="relative mb-3">
                                            <UserAvatar
                                                src={topThree[1].avatar_url || undefined}
                                                fallback={topThree[1].display_name[0]}
                                                size="lg"
                                                borderStyle={topThree[1].borderStyle}
                                                className={!topThree[1].borderStyle ? "group-hover:ring-slate-300/50 transition-all duration-300" : "transition-all duration-300"}
                                            />
                                        </div>
                                        <h3 className="font-bold text-lg group-hover:text-primary transition-colors mb-4">{topThree[1].display_name}</h3>
                                        <div className="w-full h-32 bg-gradient-to-t from-slate-500/10 to-slate-500/5 rounded-t-xl flex flex-col items-center justify-center gap-2">
                                            <div className="bg-slate-300 text-slate-800 w-10 h-10 rounded-full flex items-center justify-center font-black shadow-lg text-lg">2</div>
                                            <span className="text-white text-md font-bold">{topThree[1].formattedValue}</span>
                                        </div>
                                    </div>
                                )}

                                {/* First Place */}
                                {topThree[0] && (
                                    <div className="flex flex-col items-center group cursor-pointer -mt-8" onClick={() => handlePlayerClick(topThree[0].display_name)}>
                                        <div className="relative mb-4">
                                            <div className="absolute z-10 -top-7 left-1/2 -translate-x-1/2 text-2xl animate-bounce">👑</div>
                                            <UserAvatar
                                                src={topThree[0].avatar_url || undefined}
                                                fallback={topThree[0].display_name[0]}
                                                size="lg"
                                                borderStyle={topThree[0].borderStyle}
                                                className={!topThree[0].borderStyle ? "ring-offset-4 ring-offset-background group-hover:ring-yellow-500/50 transition-all duration-300 scale-110" : "transition-all duration-300 scale-110"}
                                            />
                                        </div>
                                        <h3 className="font-bold text-2xl group-hover:text-primary transition-colors mb-6">{topThree[0].display_name}</h3>
                                        <div className="w-full h-48 bg-gradient-to-t from-primary/20 to-primary/5 rounded-t-xl shadow-2xl flex flex-col items-center justify-center gap-3">
                                            <div className="bg-yellow-500 text-white w-14 h-14 rounded-full flex items-center justify-center font-black shadow-xl text-3xl border-4 border-yellow-400">1</div>
                                            <span className="text-white text-md font-bold">{topThree[0].formattedValue}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Third Place */}
                                {topThree[2] && (
                                    <div className="flex flex-col items-center group cursor-pointer" onClick={() => handlePlayerClick(topThree[2].display_name)}>
                                        <div className="relative mb-3">
                                            <UserAvatar
                                                src={topThree[2].avatar_url || undefined}
                                                fallback={topThree[2].display_name[0]}
                                                size="md"
                                                borderStyle={topThree[2].borderStyle}
                                                className={!topThree[2].borderStyle ? "group-hover:ring-amber-600/50 transition-all duration-300" : "transition-all duration-300"}
                                            />
                                        </div>
                                        <h3 className="font-bold text-md group-hover:text-primary transition-colors mb-4">{topThree[2].display_name}</h3>
                                        <div className="w-full h-24 bg-gradient-to-t from-amber-900/10 to-amber-900/5 rounded-t-xl flex flex-col items-center justify-center gap-2">
                                            <div className="bg-amber-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-black shadow-lg text-sm">3</div>
                                            <span className="text-white text-md font-bold">{topThree[2].formattedValue}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* List Section - All on Mobile, Ranks 4+ on Desktop (unless page > 1) */}
                        <div className="grid grid-cols-1 gap-3">
                            {players.map((player, index) => {
                                const rank = (page - 1) * limit + index + 1;
                                const isFirst = rank === 1;
                                const isSecond = rank === 2;
                                const isThird = rank === 3;
                                const isTopThree = rank <= 3;

                                return (
                                    <Card
                                        key={player.id}
                                        className={`p-3 flex items-center justify-between hover:bg-muted/30 transition-all cursor-pointer group border-primary/5 ${isTopThree && page === 1 ? 'md:hidden' : ''}`}
                                        onClick={() => handlePlayerClick(player.display_name)}
                                    >
                                        <div className="flex items-center gap-3 md:gap-4">
                                            <div className={`w-6 md:w-8 text-center font-bold flex items-center justify-center ${isFirst ? 'text-yellow-500 text-lg md:text-xl' :
                                                isSecond ? 'text-slate-400 text-base md:text-lg' :
                                                    isThird ? 'text-amber-600 text-base md:text-lg' :
                                                        'text-muted-foreground/50 text-sm md:text-base'
                                                }`}>
                                                {isFirst ? '👑' : rank}
                                            </div>
                                            <UserAvatar
                                                src={player.avatar_url || undefined}
                                                fallback={player.display_name[0]}
                                                size={isTopThree ? "md" : "sm"}
                                                borderStyle={player.borderStyle}
                                                className={
                                                    !player.borderStyle ? (
                                                        isFirst ? "ring-2 ring-yellow-500/50" :
                                                            isSecond ? "ring-2 ring-slate-400/50" :
                                                                isThird ? "ring-2 ring-amber-600/50" :
                                                                    ""
                                                    ) : ""
                                                }
                                            />
                                            <span className={`font-medium group-hover:text-primary transition-colors truncate ${isTopThree ? 'text-base md:text-lg font-bold' : 'text-sm md:text-base'}`}>
                                                {player.display_name}
                                                {isFirst && <span className="ml-1 hidden sm:inline-block">🏆</span>}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 md:gap-4 ml-2 flex-shrink-0">
                                            <div className="flex flex-col items-end">
                                                <span className={`font-bold whitespace-nowrap ${isFirst ? 'text-base md:text-xl text-yellow-500' : isTopThree ? 'text-sm md:text-lg' : 'text-sm'}`}>
                                                    {player.formattedValue}
                                                </span>
                                            </div>
                                            <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground opacity-30 md:opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-4 mt-8 pt-8 border-t border-border/50">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="gap-2"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </Button>

                                <span className="text-sm text-muted-foreground">
                                    Page <span className="font-bold text-foreground">{page}</span> of <span className="font-bold text-foreground">{totalPages}</span>
                                </span>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="gap-2"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
